import axios from 'axios';
import { api } from './gatewayApi';
import { API_ENDPOINTS } from '../config/api';

export interface AirportSuggestion {
  iataCode: string;
  name: string;
  cityName: string;
  countryCode: string;
  countryName: string;
  type: string;
  displayLabel: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  error?: string;
}

interface ApiErrorPayload {
  message?: string;
  error?: string;
}

function extractApiErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as ApiErrorPayload | undefined;
    return payload?.error || payload?.message || fallbackMessage;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallbackMessage;
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Builds the "City, Airport Name (CODE)" label, matching the server's rule:
 * the city segment is omitted when it is absent or when the airport name
 * already leads with it, so we never render a stray ", " or "Boston, Boston".
 */
export function buildAirportLabel(airport: {
  iataCode: string;
  name: string;
  cityName: string;
}): string {
  const city = airport.cityName.trim();
  const name = airport.name.trim();
  const redundantCity =
    !city || name.toLowerCase().startsWith(city.toLowerCase());
  const primary = redundantCity ? name : `${city}, ${name}`;
  return primary ? `${primary} (${airport.iataCode})` : airport.iataCode;
}

/**
 * The airport-search route is a public gateway route served by trip-service,
 * so payload shape is normalized defensively here rather than trusted.
 */
function normalizeSuggestion(item: unknown): AirportSuggestion | null {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const source = item as Record<string, unknown>;
  const iataCode = readString(source, 'iataCode').toUpperCase();
  if (!iataCode) {
    return null;
  }
  const name = readString(source, 'name');
  const cityName = readString(source, 'cityName');
  const displayLabel =
    readString(source, 'displayLabel') ||
    buildAirportLabel({ iataCode, name, cityName });

  return {
    iataCode,
    name,
    cityName,
    countryCode: readString(source, 'countryCode').toUpperCase(),
    countryName: readString(source, 'countryName'),
    type: readString(source, 'type'),
    displayLabel,
  };
}

function normalizeSuggestions(payload: unknown): AirportSuggestion[] {
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as ApiResponse<unknown> | undefined)?.data)
      ? ((payload as ApiResponse<unknown[]>).data as unknown[])
      : [];
  return items
    .map(normalizeSuggestion)
    .filter((item): item is AirportSuggestion => item !== null);
}

export async function searchAirports(
  keyword: string,
  limit: number = 8
): Promise<AirportSuggestion[]> {
  const trimmed = keyword.trim();
  if (!trimmed) {
    return [];
  }
  try {
    const response = await api.get<unknown>(
      API_ENDPOINTS.TRIPS.AIRPORT_SEARCH,
      {
        params: { keyword: trimmed, limit },
      }
    );
    return normalizeSuggestions(response.data);
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to search airports'));
  }
}
